import { useEffect, useRef } from 'react';

// A slow poll is never overlapped by the next one.
export function usePolling(poll: () => Promise<void> | void, intervalMs: number, active: boolean): void {
  const pollRef = useRef(poll);
  pollRef.current = poll;

  useEffect(() => {
    if (!active) {
      return undefined;
    }

    let timer: ReturnType<typeof setInterval> | undefined;
    let isPolling = false;

    async function pollOnce() {
      if (isPolling) {
        return;
      }
      isPolling = true;
      try {
        await pollRef.current();
      } finally {
        isPolling = false;
      }
    }

    function start() {
      if (timer === undefined && document.visibilityState === 'visible') {
        timer = setInterval(pollOnce, intervalMs);
      }
    }

    function stop() {
      clearInterval(timer);
      timer = undefined;
    }

    function followVisibility() {
      if (document.visibilityState === 'visible') {
        pollOnce();
        start();
      } else {
        stop();
      }
    }

    start();
    document.addEventListener('visibilitychange', followVisibility);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', followVisibility);
    };
  }, [active, intervalMs]);
}
